// MST
import { types, flow, cast, detach } from 'mobx-state-tree';

// Interfaces
import {
  IAssessmentPreparationCommentGetListAttrs,
  IAssessmentPreparationCommentUpdateAttrs,
  IAssessmentPreparationCommentUpdateManyAttrs,
} from 'interfaces/back-office/assessment/preparation/comment/comment-view.interface';
import {
  IAssessmentPreparationCommentApiListResponse,
  IAssessmentPreparationCommentApiNullResponse,
} from 'interfaces/back-office/assessment/preparation/comment/comment-api.interface';
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';

// Utils
import {
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_preconditionFormCommentModel,
} from 'models/extensions/helper';
import { dateUtils_formatDate } from 'utils/common/date.utils';

// Lodash
import cloneDeep from 'lodash/cloneDeep';

// Constant
import { LOADING } from 'constant/shared.constant';

// Types
const { optional, model, array } = types;

// Loading
const { BASE, ACTION } = LOADING;

// Base Model
const AssessmentPreparationCommentModel = model('AssessmentPreparationComment')
  .props({
    assessmentPreparationComment_loading: withHelper_commonLoadingModel,
    assessmentPreparationComment_list: optional(array(withHelper_preconditionFormCommentModel), []),
  })
  .actions(self => ({
    assessmentPreparationComment_MAP_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep({ ...self.assessmentPreparationComment_loading, [payload.type]: payload.value });

      detach(self.assessmentPreparationComment_loading);
      self.assessmentPreparationComment_loading = cast(map);
    },
    assessmentPreparationComment_MAP_LIST_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep(
        self.assessmentPreparationComment_list.map(result =>
          result.id === payload.id ? { ...result, loading: withHelper_commonMapLoading(result.loading, payload) } : result,
        ),
      );

      detach(self.assessmentPreparationComment_list);
      self.assessmentPreparationComment_list = cast(map);
    },
    assessmentPreparationComment_SET_LIST: (payload: IAssessmentPreparationCommentApiListResponse['res']['data']): void => {
      self.assessmentPreparationComment_list = cast(
        payload.map(list => ({
          ...list,
          date: dateUtils_formatDate(list.date!, { tableFormat: true, IsComment: true, now: true }),
        })),
      );
    },
    assessmentPreparationComment_MAP_LIST: (payload: { id: number; isChecked: boolean }): void => {
      const map = cloneDeep(
        self.assessmentPreparationComment_list.map(list => {
          if (list.id === payload.id) {
            return {
              ...list,
              isChecked: payload.isChecked,
            };
          } else {
            return list;
          }
        }),
      );

      detach(self.assessmentPreparationComment_list);
      self.assessmentPreparationComment_list = cast(map);
    },
    assessmentPreparationComment_CLEAR_LIST: (): void => {
      self.assessmentPreparationComment_list = cast([]);
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment preparation comment list
     *
     * @param {IAssessmentPreparationCommentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparationComment_fetchList: flow(function* (payload: IAssessmentPreparationCommentGetListAttrs) {
      self.assessmentPreparationComment_MAP_LOADING({ type: BASE, value: true });

      const response: IAssessmentPreparationCommentApiListResponse =
        yield self.environment.assessmentPreparationCommentApi.assessmentPreparationComment_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessmentPreparationComment_SET_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparationComment_MAP_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Update assessment preparation comment
     *
     * @param {IAssessmentPreparationCommentUpdateAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparationComment_update: flow(function* (payload: IAssessmentPreparationCommentUpdateAttrs) {
      const { commentId: id } = payload.params;
      self.assessmentPreparationComment_MAP_LIST_LOADING({ id, type: ACTION, value: true });
      self.assessmentPreparationComment_MAP_LIST({ id, isChecked: payload.body.isChecked });

      const response: IAssessmentPreparationCommentApiNullResponse =
        yield self.environment.assessmentPreparationCommentApi.assessmentPreparationComment_update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        self.assessmentPreparationComment_MAP_LIST({ id, isChecked: !payload.body.isChecked });

        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparationComment_MAP_LIST_LOADING({ id, type: ACTION, value: false });
    }),
    /**
     * @description Update many assessment preparation comment
     *
     * @param {IAssessmentPreparationCommentUpdateAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparationComment_updateMany: flow(function* (payload: IAssessmentPreparationCommentUpdateManyAttrs) {
      payload.body.commentList.forEach(el => {
        self.assessmentPreparationComment_MAP_LIST_LOADING({ id: el.commentId, type: ACTION, value: true });
      });
      // self.assessmentPreparationComment_MAP_LIST({ id, isChecked: payload.body.isChecked });

      const response: IAssessmentPreparationCommentApiNullResponse =
        yield self.environment.assessmentPreparationCommentApi.assessmentPreparationComment_update_many({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        // self.assessmentPreparationComment_MAP_LIST({ id, isChecked: !payload.body.isChecked });
        if (payload?.onError) payload.onError();
      }
      payload.body.commentList.forEach(el => {
        self.assessmentPreparationComment_MAP_LIST_LOADING({ id: el.commentId, type: ACTION, value: false });
      });
    }),
  }));

export const createAssessmentPreparationCommentStoreDefaultModel = () => optional(AssessmentPreparationCommentModel, {});
